/**
 * usePausableInterval — setInterval that actually stops firing while
 * the app is backgrounded.
 *
 * A poll left running behind the scenes (live-game scores, unread
 * badges, message threads) burns battery and mobile data for a screen
 * nobody is looking at. This clears the interval on
 * `document.visibilitychange` (fires in both the web tab case and
 * inside Capacitor's WKWebView) and ALSO listens for Capacitor's
 * native `appStateChange` as a belt-and-suspenders — some iOS/WebView
 * versions suspend JS timers without reliably firing the DOM
 * visibility event first. Fires once immediately when the app becomes
 * visible again so a paused poll doesn't leave stale data on screen
 * for up to a full interval after the user returns.
 */
import { useEffect, useRef } from 'react';

export function usePausableInterval(callback, delayMs, opts = {}) {
  const { immediate = true, enabled = true } = opts;
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!enabled || !delayMs) return undefined;
    let intervalId = null;

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => savedCallback.current(), delayMs);
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const isHidden = () => (typeof document !== 'undefined' && document.visibilityState === 'hidden');

    const onVisibility = () => {
      if (isHidden()) { stop(); return; }
      if (immediate) savedCallback.current();
      start();
    };

    if (!isHidden()) {
      if (immediate) savedCallback.current();
      start();
    }
    document.addEventListener('visibilitychange', onVisibility);

    let removeAppStateListener = null;
    (async () => {
      try {
        if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
          const { App } = await import('@capacitor/app');
          const sub = await App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) onVisibility(); else stop();
          });
          removeAppStateListener = () => sub.remove();
        }
      } catch { /* plugin unavailable — visibilitychange alone still covers web + most native cases */ }
    })();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      removeAppStateListener?.();
    };
  }, [delayMs, enabled, immediate]);
}
